import { useEffect, useState, useCallback } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { FileText, CheckCircle2, Loader2 } from 'lucide-react'
import { motion } from 'framer-motion'
import { Progress } from '@/components/ui/progress'
import { Card } from '@/components/ui/Card'
import { Button } from '@/components/ui/Button'
import { useLegacyUpdatesStream } from '@/hooks/useLegacyUpdatesStream'
import type { LegacyUpdateEvent } from '@/hooks/useLegacyUpdatesStream'
import { documentService } from '@/services'

const STAGES = [
  'Extracting text layers...',
  'Identifying key value propositions...',
  'Mapping competitive landscape...',
  'Detecting context gaps...',
  'Finalizing knowledge graph...',
]

/** Maps SSE eventType (or status) to a stage index. */
function eventToStageIndex(event: LegacyUpdateEvent): number | null {
  const et = event.eventType ?? ''
  if (et.includes('extracting') || event.status === 'processing') return 0
  if (et.includes('value') || et.includes('classif')) return 1
  if (et.includes('compet') || et.includes('structur')) return 2
  if (et.includes('gap')) return 3
  if (et.includes('finaliz') || event.status === 'completed') return 4
  return null
}

export function AnalyzingPage() {
  const { documentId } = useParams<{ documentId: string }>()
  const navigate = useNavigate()

  const [progress, setProgress] = useState(0)
  const [stage, setStage] = useState(0)
  const [docName, setDocName] = useState<string | null>(null)
  const [failed, setFailed] = useState(false)

  // Fetch document name for display
  useEffect(() => {
    if (!documentId) return
    documentService.getDocument(documentId).then((doc) => {
      if (doc?.name) setDocName(doc.name)
    }).catch(() => {/* ignore — name is cosmetic */})
  }, [documentId])

  // Handle SSE update events
  const handleUpdate = useCallback(
    (event: LegacyUpdateEvent) => {
      if (event.status === 'failed') {
        setFailed(true)
        setProgress(100)
        return
      }

      if (event.status === 'completed') {
        setStage(STAGES.length - 1)
        setProgress(100)
        setTimeout(() => navigate('/chat', { replace: true }), 1200)
        return
      }

      // Advance stage from event
      const idx = eventToStageIndex(event)
      if (idx !== null) {
        setStage((prev) => Math.max(prev, idx))
        // Map stage → progress (each stage = 20%)
        setProgress((prev) => Math.max(prev, Math.min((idx + 1) * 20, 95)))
      }
    },
    [navigate]
  )

  // Subscribe to SSE for this document
  useLegacyUpdatesStream({
    entityTypes: ['document'],
    entityIds: documentId ? [documentId] : undefined,
    enabled: !!documentId,
    onUpdate: handleUpdate,
  })

  // Slow progress tick so UI doesn't feel frozen between SSE events
  useEffect(() => {
    if (progress >= 95 || failed) return
    const timer = setInterval(() => {
      setProgress((prev) => {
        if (prev >= 95) { clearInterval(timer); return prev }
        return prev + 0.5
      })
    }, 500)
    return () => clearInterval(timer)
  }, [progress, failed])

  return (
    <div className="min-h-screen w-full flex items-center justify-center p-4 bg-background selection:bg-primary/20 relative overflow-hidden">
      {/* Background glow */}
      <div className="absolute inset-0 z-0 overflow-hidden pointer-events-none">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-primary/5 rounded-full blur-[100px] opacity-50 animate-pulse" />
      </div>

      <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="w-full max-w-md z-10">
        <Card className="p-8 border-none bg-card/50 backdrop-blur-sm shadow-2xl relative overflow-hidden">
          {/* Scanning line */}
          <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-primary/50 to-transparent animate-[shimmer_2s_infinite]" />

          <div className="flex flex-col items-center text-center mb-8">
            <div className="relative mb-6">
              <div className="h-20 w-20 bg-secondary/50 rounded-2xl flex items-center justify-center border border-white/5 relative z-10">
                <FileText className={`h-10 w-10 text-primary ${failed ? '' : 'animate-pulse'}`} />
              </div>
              <div className="absolute inset-0 bg-primary/20 blur-xl rounded-full -z-10 animate-pulse" />
            </div>

            <h2 className="text-2xl font-semibold mb-2">
              {failed ? 'Processing Failed' : `Analyzing ${docName ?? 'Document'}`}
            </h2>
            <p className="text-muted-foreground h-6 transition-all duration-300">
              {failed ? 'Something went wrong. Try uploading again.' : STAGES[Math.min(stage, STAGES.length - 1)]}
            </p>
          </div>

          <div className="space-y-6">
            {/* Progress bar */}
            <div className="space-y-2">
              <div className="flex justify-between text-xs font-medium text-muted-foreground uppercase tracking-wider">
                <span>Progress</span>
                <span>{Math.round(progress)}%</span>
              </div>
              <Progress value={progress} className="h-2" />
            </div>

            {/* Stage checklist */}
            <div className="space-y-3">
              {STAGES.map((s, i) => (
                <div key={i} className="flex items-center gap-3 text-sm">
                  {i < stage ? (
                    <CheckCircle2 className="h-4 w-4 text-accent-success shrink-0" />
                  ) : i === stage && !failed ? (
                    <Loader2 className="h-4 w-4 text-primary animate-spin shrink-0" />
                  ) : (
                    <div className="h-4 w-4 rounded-full border border-white/10 shrink-0" />
                  )}
                  <span className={i > stage ? 'text-muted-foreground/50' : 'text-foreground'}>{s}</span>
                </div>
              ))}
            </div>

            {/* Failure action */}
            {failed && (
              <div className="pt-2 flex justify-center">
                <Button variant="outline" onClick={() => navigate('/chat', { replace: true })}>
                  Back to Chat
                </Button>
              </div>
            )}
          </div>
        </Card>
      </motion.div>
    </div>
  )
}
